import { Box, IconButton } from "@mui/material";
import SearchInput from "../system/tenant/search-input/search-input";
import { useContext, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import styles from "./releases.module.scss";
import { useTranslation } from "react-i18next";
import { getResourceNote } from "api/layer";
import { AppAction, AppContext } from "helpers/context";
import {
  ReleaseNoteType,
  ReleaseDetail,
  ReleaseListItem,
  useFileDownload,
  getFileExtension,
  getDisplayName,
} from "components/release";
import {
  DataSourceType,
  DataSourceDetail,
  DataSourceListItem,
  getFileExtension as getDataSourceFileExtension,
  getDisplayName as getDataSourceDisplayName,
} from "components/dataSource";
import {
  FaqType,
  FaqDetail,
  FaqListItem,
  getFileExtension as getFaqFileExtension,
  getDisplayName as getFaqDisplayName,
} from "components/faq";

const langEn = "en";
const langCn = "cn";

type CategoryType = "DATA_SOURCE" | "RELEASE_INFO" | "FAQ";

// Map API response to ReleaseNoteType
const mapApiToReleaseNote = (item: any): ReleaseNoteType => {
  const attachments = item.attachments || [];
  const files = Array.isArray(attachments)
    ? attachments.map((attachment: string, index: number) => {
        const fileName = attachment.split("/").pop() || attachment;
        return {
          id: `f${index + 1}`,
          name: getDisplayName(attachment),
          size: "",
          type: getFileExtension(fileName),
          url: attachment,
        };
      })
    : undefined;

  return {
    id: String(item.resourceId),
    version: item.resourceVersion || "",
    title: item.resourceTitle || "",
    content: item.resourceContent || "",
    attachments: attachments,
    files,
    status: item.status || "publish",
  };
};

// Map API response to DataSourceType
const mapApiToDataSource = (item: any): DataSourceType => {
  const attachments = item.attachments || [];
  const files = Array.isArray(attachments)
    ? attachments.map((attachment: string, index: number) => {
        const fileName = attachment.split("/").pop() || attachment;
        return {
          id: `f${index + 1}`,
          name: getDataSourceDisplayName(attachment),
          size: "",
          type: getDataSourceFileExtension(fileName),
          url: attachment,
        };
      })
    : undefined;

  return {
    id: String(item.resourceId),
    version: item.resourceVersion || "",
    title: item.resourceTitle || "",
    content: item.resourceContent || "",
    attachments: attachments,
    files,
    status: item.status || "publish",
  };
};

// Map API response to FaqType
const mapApiToFaq = (item: any): FaqType => {
  const attachments = item.attachments || [];
  const files = Array.isArray(attachments)
    ? attachments.map((attachment: string, index: number) => {
        const fileName = attachment.split("/").pop() || attachment;
        return {
          id: `f${index + 1}`,
          name: getFaqDisplayName(attachment),
          size: "",
          type: getFaqFileExtension(fileName),
          url: attachment,
        };
      })
    : undefined;

  return {
    id: String(item.resourceId),
    version: item.resourceVersion || "",
    title: item.resourceTitle || "",
    content: item.resourceContent || "",
    attachments: attachments,
    files,
    status: item.status || "publish",
  };
};

function Resources() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [context, dispatch] = useContext(AppContext);

  const changeLanguage = (lang: string): void => {
    i18n.changeLanguage(lang);
    dispatch({ type: AppAction.setLanguage, payload: lang });
  };

  const getLangClass = (lang: string): string =>
    context.language === lang ? styles.langSelected : styles.lang;

  const [releaseNotes, setReleaseNotes] = useState<ReleaseNoteType[]>([]);
  const [dataSources, setDataSources] = useState<DataSourceType[]>([]);
  const [faqs, setFaqs] = useState<FaqType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryType>("RELEASE_INFO");
  const [selectedRelease, setSelectedRelease] =
    useState<ReleaseNoteType | null>(null);
  const [selectedDataSource, setSelectedDataSource] =
    useState<DataSourceType | null>(null);
  const [selectedFaq, setSelectedFaq] = useState<FaqType | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<{
    DATA_SOURCE: boolean;
    RELEASE_INFO: boolean;
    FAQ: boolean;
  }>({
    DATA_SOURCE: false,
    RELEASE_INFO: true,
    FAQ: false,
  });

  const [showFilters, setShowFilters] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");

  const { downloadingFile, handleDownloadFile } = useFileDownload({ dispatch });

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
  };

  const toggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const clearFilters = () => {
    setSearchKeyword("");
    setShowFilters(false);
  };

  const handleGetReleaseList = async () => {
    setLoading(true);
    try {
      // Fetch release notes, data sources, and FAQs, only apply keyword to the active category
      const [releaseRes, dataSourceRes, faqRes]: any = await Promise.all([
        getResourceNote(
          "RELEASE_INFO",
          selectedCategory === "RELEASE_INFO" ? searchKeyword : ""
        ),
        getResourceNote(
          "DATA_SOURCE",
          selectedCategory === "DATA_SOURCE" ? searchKeyword : ""
        ),
        getResourceNote("FAQ", selectedCategory === "FAQ" ? searchKeyword : ""),
      ]);

      // Process release notes
      const releaseDetails =
        releaseRes?.data?.releaseDetails || releaseRes?.releaseDetails || [];
      if (Array.isArray(releaseDetails)) {
        const publishedReleases = releaseDetails.filter(
          (item: any) => item.status === "publish"
        );
        setReleaseNotes(publishedReleases.map(mapApiToReleaseNote));
      } else {
        setReleaseNotes([]);
      }

      // Process data sources
      const dataSourceDetails =
        dataSourceRes?.data?.releaseDetails ||
        dataSourceRes?.releaseDetails ||
        [];
      if (Array.isArray(dataSourceDetails)) {
        const publishedDataSources = dataSourceDetails.filter(
          (item: any) => item.status === "publish"
        );
        setDataSources(publishedDataSources.map(mapApiToDataSource));
      } else {
        setDataSources([]);
      }

      // Process FAQs
      const faqDetails =
        faqRes?.data?.releaseDetails || faqRes?.releaseDetails || [];
      if (Array.isArray(faqDetails)) {
        const publishedFaqs = faqDetails.filter(
          (item: any) => item.status === "publish"
        );
        setFaqs(publishedFaqs.map(mapApiToFaq));
      } else {
        setFaqs([]);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetReleaseList();
  }, [searchKeyword, selectedCategory]);

  // Handle URL param to set category based on type
  useEffect(() => {
    const type = searchParams.get("type");
    if (type === "release") {
      setSelectedCategory("RELEASE_INFO");
      setExpandedCategories({ DATA_SOURCE: false, RELEASE_INFO: true, FAQ: false });
    } else if (type === "dataSource") {
      setSelectedCategory("DATA_SOURCE");
      setExpandedCategories({ DATA_SOURCE: true, RELEASE_INFO: false, FAQ: false });
    } else if (type === "faq") {
      setSelectedCategory("FAQ");
      setExpandedCategories({ DATA_SOURCE: false, RELEASE_INFO: false, FAQ: true });
    }
  }, [searchParams]);

  // Handle URL param to auto-select release or data source by id
  useEffect(() => {
    const releaseId = searchParams.get("id");
    if (releaseId) {
      if (selectedCategory === "RELEASE_INFO") {
        const release = releaseNotes.find((r) => r.id === releaseId);
        if (release) {
          setSelectedRelease(release);
        }
      } else if (selectedCategory === "DATA_SOURCE") {
        const dataSource = dataSources.find((d) => d.id === releaseId);
        if (dataSource) {
          setSelectedDataSource(dataSource);
        }
      } else if (selectedCategory === "FAQ") {
        const faq = faqs.find((f) => f.id === releaseId);
        if (faq) {
          setSelectedFaq(faq);
        }
      }
    }
  }, [searchParams, releaseNotes, dataSources, faqs, selectedCategory]);

  const handleReleaseClick = (release: ReleaseNoteType) => {
    setSelectedRelease(release);
  };

  const handleBackToList = () => {
    setSelectedRelease(null);
    setSelectedDataSource(null);
    setSelectedFaq(null);
    setSearchParams({});
  };

  const handleDataSourceClick = (dataSource: DataSourceType) => {
    setSelectedDataSource(dataSource);
  };

  const handleCategoryChange = (category: CategoryType) => {
    setSelectedCategory(category);
    setSearchKeyword("");
  };

  const toggleCategory = (category: CategoryType) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleFaqClick = (faq: FaqType) => {
    setSelectedFaq(faq);
  };

  const handleChildItemClick = (
    category: CategoryType,
    item: ReleaseNoteType | DataSourceType | FaqType
  ) => {
    setSelectedCategory(category);
    setSearchKeyword("");
    if (category === "RELEASE_INFO") {
      setSelectedRelease(item as ReleaseNoteType);
    } else if (category === "DATA_SOURCE") {
      setSelectedDataSource(item as DataSourceType);
    } else {
      setSelectedFaq(item as FaqType);
    }
  };

  const currentList =
    selectedCategory === "RELEASE_INFO"
      ? releaseNotes
      : selectedCategory === "DATA_SOURCE"
        ? dataSources
        : faqs;

  const filteredList = currentList;

  const headerContent = (
    <div className={styles.header}>
      <div className={styles.logo}>
        <Link to="/" className="pr-6">
          <img alt="SwissRe_Logo" src="/SwissRe_Logo.svg" />
        </Link>
        <div className="text-[19px]">
          CatNet<sup>®</sup>
        </div>
      </div>
      <div className="flex">
        <div
          className={getLangClass(langEn)}
          onClick={() => changeLanguage(langEn)}
        >
          {t("header.lang.en")}
        </div>
        <div
          className={getLangClass(langCn)}
          onClick={() => changeLanguage(langCn)}
        >
          {t("header.lang.cn")}
        </div>
      </div>
    </div>
  );

  if (selectedRelease) {
    return (
      <ReleaseDetail
        release={selectedRelease}
        downloadingFile={downloadingFile}
        onDownloadFile={handleDownloadFile}
        attachmentsLabel={t("releases.downloads")}
        containerClassName={styles.container}
        contentClassName="w-full py-8 px-10 max-w-[900px] mx-auto bg-white rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.08),_0_2px_8px_rgba(0,0,0,0.06)] border border-[#e8e8e8] my-6"
        headerContent={headerContent}
        actionsContent={
          <IconButton
            onClick={handleBackToList}
            className="!text-[#00463B]"
            aria-label="back"
          >
            <span className="material-icons text-[24px]">arrow_back</span>
            <span className="ml-2 text-base">{t("buttons.back")}</span>
          </IconButton>
        }
      />
    );
  }

  if (selectedDataSource) {
    return (
      <DataSourceDetail
        dataSource={selectedDataSource}
        downloadingFile={downloadingFile}
        onDownloadFile={handleDownloadFile}
        attachmentsLabel={t("releases.downloads")}
        containerClassName={styles.container}
        contentClassName="w-full py-8 px-10 max-w-[900px] mx-auto bg-white rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.08),_0_2px_8px_rgba(0,0,0,0.06)] border border-[#e8e8e8] my-6"
        headerContent={headerContent}
        actionsContent={
          <IconButton
            onClick={handleBackToList}
            className="!text-[#00463B]"
            aria-label="back"
          >
            <span className="material-icons text-[24px]">arrow_back</span>
            <span className="ml-2 text-base">{t("buttons.back")}</span>
          </IconButton>
        }
      />
    );
  }

  if (selectedFaq) {
    return (
      <FaqDetail
        faq={selectedFaq}
        downloadingFile={downloadingFile}
        onDownloadFile={handleDownloadFile}
        attachmentsLabel={t("releases.downloads")}
        containerClassName={styles.container}
        contentClassName="w-full py-8 px-10 max-w-[900px] mx-auto bg-white rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.08),_0_2px_8px_rgba(0,0,0,0.06)] border border-[#e8e8e8] my-6"
        headerContent={headerContent}
        actionsContent={
          <IconButton
            onClick={handleBackToList}
            className="!text-[#00463B]"
            aria-label="back"
          >
            <span className="material-icons text-[24px]">arrow_back</span>
            <span className="ml-2 text-base">{t("buttons.back")}</span>
          </IconButton>
        }
      />
    );
  }

  return (
    <div className={styles.container}>
      {headerContent}
      <div className={styles.mainContent}>
        {/* Left Sidebar */}
        <div className={styles.sidebar}>
          <h3>{t("releases.categories") || "Categories"}</h3>

          {/* Data Sources Category */}
          <div className={styles.categoryGroup}>
            <div
              className={`${styles.sidebarItem} ${selectedCategory === "DATA_SOURCE" ? styles.active : ""}`}
              onClick={() => handleCategoryChange("DATA_SOURCE")}
            >
              <div className={styles.categoryHeader}>
                <span
                  className={`material-icons ${styles.expandIcon}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCategory("DATA_SOURCE");
                  }}
                >
                  {expandedCategories.DATA_SOURCE
                    ? "expand_more"
                    : "chevron_right"}
                </span>
                <span>{t("releases.dataSources") || "Data Sources"}</span>
              </div>
              <span className={styles.sidebarCount}>{dataSources.length}</span>
            </div>
            {expandedCategories.DATA_SOURCE && dataSources.length > 0 && (
              <div className={styles.childrenList}>
                {dataSources.map((item) => (
                  <div
                    key={item.id}
                    className={styles.childItem}
                    onClick={() => handleChildItemClick("DATA_SOURCE", item)}
                  >
                    {item.title}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Release Info Category */}
          <div className={styles.categoryGroup}>
            <div
              className={`${styles.sidebarItem} ${selectedCategory === "RELEASE_INFO" ? styles.active : ""}`}
              onClick={() => handleCategoryChange("RELEASE_INFO")}
            >
              <div className={styles.categoryHeader}>
                <span
                  className={`material-icons ${styles.expandIcon}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCategory("RELEASE_INFO");
                  }}
                >
                  {expandedCategories.RELEASE_INFO
                    ? "expand_more"
                    : "chevron_right"}
                </span>
                <span>{t("releases.releaseInfo") || "Release Info"}</span>
              </div>
              <span className={styles.sidebarCount}>{releaseNotes.length}</span>
            </div>
            {expandedCategories.RELEASE_INFO && releaseNotes.length > 0 && (
              <div className={styles.childrenList}>
                {releaseNotes.map((item) => (
                  <div
                    key={item.id}
                    className={styles.childItem}
                    onClick={() => handleChildItemClick("RELEASE_INFO", item)}
                  >
                    {item.title}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* FAQ Category */}
          <div className={styles.categoryGroup}>
            <div
              className={`${styles.sidebarItem} ${selectedCategory === "FAQ" ? styles.active : ""}`}
              onClick={() => handleCategoryChange("FAQ")}
            >
              <div className={styles.categoryHeader}>
                <span
                  className={`material-icons ${styles.expandIcon}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCategory("FAQ");
                  }}
                >
                  {expandedCategories.FAQ ? "expand_more" : "chevron_right"}
                </span>
                <span>{t("releases.faqs") || "FAQ"}</span>
              </div>
              <span className={styles.sidebarCount}>{faqs.length}</span>
            </div>
            {expandedCategories.FAQ && faqs.length > 0 && (
              <div className={styles.childrenList}>
                {faqs.map((item) => (
                  <div
                    key={item.id}
                    className={styles.childItem}
                    onClick={() => handleChildItemClick("FAQ", item)}
                  >
                    {item.title}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* List Section */}
        <div className={styles.listSection}>
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-[#00463B]">
              {selectedCategory === "RELEASE_INFO"
                ? t("releases.title")
                : selectedCategory === "DATA_SOURCE"
                  ? t("releases.dataSourcesTitle") || "Data Sources"
                  : t("releases.faqsTitle") || "FAQ"}
            </h1>
            <IconButton
              color="success"
              onClick={toggleFilters}
              style={{
                color: showFilters ? "#2e7d32" : "rgba(0, 0, 0, 0.54)",
                background: showFilters ? "#c1e5ca" : "",
              }}
            >
              <span className="material-icons">filter_list</span>
            </IconButton>
          </div>
          {showFilters && (
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                padding: "8px",
                backgroundColor: "#f5f5f5",
                justifyContent: "space-between",
                marginBottom: "8px",
                borderRadius: "4px",
              }}
            >
              <Box style={{ display: "flex", flex: 1, alignItems: "center" }}>
                <SearchInput key={selectedCategory} onSearch={handleSearch} />
              </Box>
              <IconButton
                onClick={clearFilters}
                aria-label="clear"
                color="default"
              >
                <span className="material-icons">clear</span>
              </IconButton>
            </Box>
          )}
          <div className="w-full h-auto py-4 flex flex-col bg-white rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.12),_-2px_0_8px_rgba(0,0,0,0.06),_2px_0_8px_rgba(0,0,0,0.06)] my-4">
            {loading ? (
              <div className="w-full p-8 text-center text-[#999]">
                Loading...
              </div>
            ) : filteredList.length === 0 ? (
              <div className="w-full p-8 text-center text-[#999]">
                {t("releases.noData") || "No data found"}
              </div>
            ) : selectedCategory === "RELEASE_INFO" ? (
              (filteredList as ReleaseNoteType[]).map((release) => (
                <ReleaseListItem
                  key={release.id}
                  release={release}
                  onClick={handleReleaseClick}
                />
              ))
            ) : selectedCategory === "DATA_SOURCE" ? (
              (filteredList as DataSourceType[]).map((dataSource) => (
                <DataSourceListItem
                  key={dataSource.id}
                  dataSource={dataSource}
                  onClick={handleDataSourceClick}
                />
              ))
            ) : (
              (filteredList as FaqType[]).map((faq) => (
                <FaqListItem key={faq.id} faq={faq} onClick={handleFaqClick} />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Resources;
